import { apiClient } from '@/lib/api/client';
import { API_ENDPOINTS } from '@/lib/api/config';
import type {
  OcrTemplate,
  OcrTemplateSuggestion
} from '@/features/ocr-templates';
import type {
  ActiveOcrJob,
  ContainerPagesResponse,
  OcrContainerSummary,
  OcrResult,
  OcrResultStatusPayload,
  OcrSuggestionRunDetail,
  OcrSuggestionRunSummary,
  PageTemplateAssignment,
  PageTemplateSuggestionResponse,
  PageTemplateSuggestionsResponse,
  CreateFieldHighlightRequest,
  CreateOcrFieldRequest,
  RenameOcrFieldRequest,
  CreateOcrFieldResponse,
  DeleteFieldHighlightRequest,
  UpdateChunkBboxRequest,
  UpdateFieldRequest
} from '../types';

const { OCR } = API_ENDPOINTS;

export interface OcrTemplateSuggestionsResponse {
  /** 'pending' while the backend job is still computing; poll until 'ready'. */
  status: 'ready' | 'pending';
  suggestions: OcrTemplateSuggestion[];
}

export interface OcrContainersListParams {
  page?: number;
  per_page?: number;
  search?: string;
  ocr_status?: string | string[];
  ocr_template_id?: string[];
  date_from?: string;
  date_to?: string;
  min_pages?: number;
  max_pages?: number;
  repair_status?: string[];
  min_repair_count?: number;
  max_repair_count?: number;
  review_status?: string[];
  min_review_count?: number;
  max_review_count?: number;
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
}

export const ocrApi = {
  containers: (params?: OcrContainersListParams) =>
    apiClient.getPaginated<any>(OCR.CONTAINERS, params),

  /**
   * Create an ad-hoc OCR test container from uploaded page images and run the
   * real pipeline against the chosen templates. PDFs are rasterized to images
   * client-side, so the FormData only ever carries `images[]`. Returns the new
   * container id so the caller can navigate to the standard results page.
   */
  createTestContainer: (
    formData: FormData,
    onProgress?: (progress: number) => void
  ) =>
    apiClient.uploadFormData<{ id: number }>(
      OCR.TEST_CONTAINERS,
      formData,
      onProgress
    ),

  /**
   * Append another chunk of page images to a test container. Large documents
   * are uploaded as a sequence of ≤15-image requests (PHP's max_file_uploads
   * ceiling), so the container is created with the first chunk, then the rest
   * stream through here.
   */
  appendTestContainerPages: (
    containerId: number,
    formData: FormData,
    onProgress?: (progress: number) => void
  ) =>
    apiClient.uploadFormData<{ id: number; total_pages: number }>(
      OCR.APPEND_TEST_CONTAINER_PAGES(containerId),
      formData,
      onProgress
    ),

  /** Delete a test container and its stored pages/results. */
  deleteTestContainer: (containerId: number) =>
    apiClient.delete<unknown>(OCR.DELETE_TEST_CONTAINER(containerId)),

  /**
   * Start OCR. `pageTemplateAssignments` (the "Assign per page" flow) takes
   * precedence over `ocrTemplateIds`: when present, each page is extracted by
   * its chosen template and auto-classification is skipped.
   */
  start: (
    containerId: number,
    options?: {
      ocrTemplateIds?: number[];
      pageTemplateAssignments?: PageTemplateAssignment[];
    }
  ) => {
    let body: Record<string, unknown> | undefined;
    if (options?.pageTemplateAssignments?.length) {
      body = { page_template_assignments: options.pageTemplateAssignments };
    } else if (options?.ocrTemplateIds?.length) {
      body = { ocr_template_ids: options.ocrTemplateIds };
    }

    return apiClient.post<OcrResult[]>(OCR.START(containerId), body);
  },

  /**
   * Pause every in-flight run on the container. The backend cancels the Bus
   * batch, so queued page batches skip immediately; a batch already mid-model-call
   * call finishes and its pages are kept. 422 when nothing is running, or when
   * the run is still being prepared and has no page batches to cancel yet.
   */
  pause: (containerId: number) =>
    apiClient.post<OcrResult[]>(OCR.PAUSE(containerId)),

  /**
   * Resume every paused run on the container, re-dispatching only the pages
   * that never extracted. Pages completed before the pause keep their data.
   */
  resume: (containerId: number) =>
    apiClient.post<OcrResult[]>(OCR.RESUME(containerId)),

  /**
   * Force a stuck run to finish now: the page ranges whose workers never
   * reported back are dead-lettered and the run finalizes as
   * completed_with_errors, so those pages show up under "retry failed batches".
   * 422 when nothing is in flight on the container.
   */
  recover: (containerId: number) =>
    apiClient.post<OcrResult[]>(OCR.RECOVER(containerId)),

  /**
   * Move a container to the repair queue (records origin_stage = 'ocr').
   * `repair_destination_id` is optional for ad-hoc OCR test containers, which
   * have no physical form — the backend only requires it for real containers.
   */
  refuse: (
    containerId: number,
    data: {
      repair_destination_id?: number;
      repair_reason_id: number;
      notes?: string;
    }
  ) => apiClient.post<unknown>(OCR.REFUSE(containerId), data),

  /**
   * Submit the whole container to OCR QA (tier 1). Backend rejects with 422
   * unless every extracted field of every page is accepted, then stamps
   * qa_stage=pending_qa1 and advances the registration ocr → qa1_ocr.
   */
  submitToQa: (containerId: number) =>
    apiClient.post<OcrResult[]>(OCR.SUBMIT_TO_QA(containerId)),

  /**
   * Cheap page list for the MANUAL "Assign per page" picker — the exact set
   * startOcr() extracts, no model call. Powers the manual panel, which renders
   * before (and independent of) the AI ranking job.
   */
  getContainerPages: (containerId: number) =>
    apiClient.get<ContainerPagesResponse>(OCR.CONTAINER_PAGES(containerId)),

  /**
   * Per-page template suggestions for the "Assign per page" flow. Async
   * contract: a cold cache returns `{status: 'pending'}` and dispatches a
   * backend classification job — poll until `ready`. The page list arrives
   * with the ready payload (with fresh signed image urls).
   */
  getPageTemplateSuggestions: (
    containerId: number,
    options?: { cachedOnly?: boolean }
  ) =>
    apiClient.get<PageTemplateSuggestionsResponse>(
      OCR.PAGE_TEMPLATE_SUGGESTIONS(containerId),
      // Cache-only: answers a ranking already paid for, never starts one.
      options?.cachedOnly ? { cached_only: 1 } : undefined
    ),

  /**
   * One page's on-demand ranking. `refresh` drops the page's cached entry
   * first (retry after a failure).
   */
  getPageTemplateSuggestion: (
    containerId: number,
    pageId: number,
    options?: { refresh?: boolean }
  ) =>
    apiClient.get<PageTemplateSuggestionResponse>(
      OCR.PAGE_TEMPLATE_SUGGESTION(containerId, pageId),
      options?.refresh ? { refresh: 1 } : undefined
    ),

  /** Force a fresh per-page ranking ("+ new" in the history). */
  regeneratePageTemplateSuggestions: (containerId: number) =>
    apiClient.post<PageTemplateSuggestionsResponse>(
      OCR.REGENERATE_PAGE_TEMPLATE_SUGGESTIONS(containerId)
    ),

  /** Force a fresh pool ranking ("+ new" in the history). */
  regeneratePoolSuggestions: (registrationId: number) =>
    apiClient.post<{ status: string }>(
      OCR.REGENERATE_POOL_SUGGESTIONS(registrationId)
    ),

  /** Suggestion-run history for a registration (newest first). */
  getSuggestionRuns: (registrationId: number) =>
    apiClient.get<OcrSuggestionRunSummary[]>(
      OCR.SUGGESTION_RUNS(registrationId)
    ),

  /** Same history, addressed by container (resolves the registration server-side). */
  getContainerSuggestionRuns: (containerId: number) =>
    apiClient.get<OcrSuggestionRunSummary[]>(
      OCR.CONTAINER_SUGGESTION_RUNS(containerId)
    ),

  /** Full detail of one suggestion run (suggestions + assignment). */
  getSuggestionRun: (runId: number) =>
    apiClient.get<OcrSuggestionRunDetail>(OCR.SUGGESTION_RUN(runId)),

  /**
   * Active OCR template candidates assigned to a registration.
   */
  listRegistrationTemplates: (registrationId: number) =>
    apiClient.get<OcrTemplate[]>(
      OCR.LIST_REGISTRATION_TEMPLATES(registrationId)
    ),

  /**
   * Replace the registration's candidate template pool. Backend enforces
   * `ocr.max_candidate_templates` (default 3).
   */
  syncRegistrationTemplates: (registrationId: number, templateIds: number[]) =>
    apiClient.post<OcrTemplate[]>(
      OCR.SYNC_REGISTRATION_TEMPLATES(registrationId),
      { template_ids: templateIds }
    ),

  /**
   * AI-ranked suggestions for which OCR templates suit this registration's
   * document. Async contract: a cache miss dispatches a backend job and
   * returns `{status: 'pending'}` immediately — poll until `ready`.
   * Vendor failures surface as `ready` with an empty list — caller must
   * fall back to a manual picker.
   */
  suggestTemplates: (registrationId: number, limit?: number) =>
    apiClient.post<OcrTemplateSuggestionsResponse>(
      OCR.SUGGEST_TEMPLATES(registrationId),
      limit ? { limit } : undefined
    ),

  /**
   * The container's own identity — label, registration_id, template pool, QA
   * stage. Separate from containerResults on purpose: that payload is patched
   * in place by the websocket cache updater, so it cannot carry container
   * metadata without breaking live updates.
   */
  containerSummary: (containerId: number) =>
    apiClient.get<OcrContainerSummary>(OCR.CONTAINER(containerId)),

  containerResults: (containerId: number) =>
    apiClient.get<OcrResult[]>(OCR.CONTAINER_RESULTS(containerId)),

  /**
   * Read-only archive of OCR runs superseded when the container returned from
   * repair. Same serialization as containerResults; every result carries a
   * non-null `archived_at` so the history view can group by run date.
   */
  containerHistory: (containerId: number) =>
    apiClient.get<OcrResult[]>(OCR.CONTAINER_HISTORY(containerId)),

  getResult: (resultId: number) =>
    apiClient.get<OcrResult>(OCR.RESULT(resultId)),

  /**
   * Lightweight batch fetch for status polling. Returns minimal payload —
   * no relations, no chunks. Use for fallback polling, not for full views.
   */
  getStatuses: (ids: number[]) =>
    apiClient.get<OcrResultStatusPayload[]>(OCR.STATUSES, {
      ids: ids.join(',')
    }),

  updateField: (resultId: number, key: string, data: UpdateFieldRequest) =>
    apiClient.put<OcrResult>(OCR.UPDATE_FIELD(resultId), { key, ...data }),

  renameField: (resultId: number, data: RenameOcrFieldRequest) =>
    apiClient.put<OcrResult>(OCR.RENAME_FIELD(resultId), data),

  updateChunkBbox: (
    resultId: number,
    chunkId: number,
    data: UpdateChunkBboxRequest
  ) =>
    apiClient.patch<OcrResult>(OCR.UPDATE_CHUNK_BBOX(resultId, chunkId), data),

  createFieldHighlight: (resultId: number, data: CreateFieldHighlightRequest) =>
    apiClient.post<OcrResult>(OCR.CREATE_FIELD_HIGHLIGHT(resultId), data),

  createField: (resultId: number, data: CreateOcrFieldRequest) =>
    apiClient.post<CreateOcrFieldResponse>(OCR.CREATE_FIELD(resultId), data),

  deleteFieldHighlight: (
    resultId: number,
    chunkId: number,
    data: DeleteFieldHighlightRequest
  ) =>
    apiClient.delete<OcrResult>(
      OCR.DELETE_FIELD_HIGHLIGHT(resultId, chunkId),
      data
    ),

  acceptField: (resultId: number, key: string, pageNumber?: number | null) =>
    apiClient.post<OcrResult>(OCR.ACCEPT_FIELD(resultId), {
      key,
      ...(pageNumber != null ? { page_number: pageNumber } : {})
    }),

  acceptAll: (resultId: number) =>
    apiClient.post<OcrResult>(OCR.ACCEPT_ALL(resultId)),

  acceptAllOnPage: (resultId: number, page: number) =>
    apiClient.post<OcrResult>(OCR.ACCEPT_ALL_ON_PAGE(resultId, page)),

  retry: (resultId: number) => apiClient.post<OcrResult>(OCR.RETRY(resultId)),

  retryFailedBatches: (resultId: number) =>
    apiClient.post<OcrResult>(OCR.RETRY_FAILED_BATCHES(resultId)),

  /**
   * Re-extract ONE page of a QA-rejected run. `pageNumber` is the 1-based
   * document-global page the results page renders. Every other page keeps its
   * extraction, chunks and QA history.
   */
  rerunPage: (resultId: number, pageNumber: number) =>
    apiClient.post<OcrResult>(OCR.RERUN_PAGE(resultId, pageNumber)),

  /**
   * Re-extract only the selected pages of the container's current run, each
   * with its chosen template, merging them back so every other page keeps its
   * extraction (OMA-674). 409 while the run is still in flight.
   */
  rerunPages: (containerId: number, pages: PageTemplateAssignment[]) =>
    apiClient.post<OcrResult[]>(OCR.RERUN_PAGES(containerId), { pages }),

  /**
   * In-flight OCR jobs for the current user — feeds the dashboard
   * header indicator. Filtered backend-side to status in (pending, processing).
   */
  activeJobs: () => apiClient.get<ActiveOcrJob[]>(OCR.ACTIVE_JOBS)
};
